import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  EmailTemplate,
  getTemplateCategories,
  getTemplatesByCategory,
} from "./emailTemplates";

interface EmailTemplatesDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (template: EmailTemplate) => void;
}

const EmailTemplatesDialog: React.FC<EmailTemplatesDialogProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const categories = getTemplateCategories();
  const [expandedCategory, setExpandedCategory] = useState<string | false>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);

  // Reset selection when dialog opens
  useEffect(() => {
    if (open) {
      setExpandedCategory(false);
      setSelectedTemplate(null);
    }
  }, [open]);

  const handleAccordionChange = (category: string) => (
    _event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedCategory(isExpanded ? category : false);
  };

  const handleUseTemplate = () => {
    if (selectedTemplate) {
      onSelect(selectedTemplate);
      onClose();
    }
  };

  // Format template body for preview (convert \n to actual newlines)
  const formatBody = (body: string) => {
    return body.replace(/\\n/g, "\n");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          height: "80vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Email Templates</Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          p: 0,
        }}
      >
        <Box sx={{ display: "flex", width: "100%", height: "100%" }}>
          {/* Left panel - Categories as Accordions with Templates */}
          <Box
            sx={{
              width: 320,
              minWidth: 320,
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
            }}
          >
            {categories.map((category) => {
              const templates = getTemplatesByCategory(category);
              return (
                <Accordion
                  key={category}
                  expanded={expandedCategory === category}
                  onChange={handleAccordionChange(category)}
                  disableGutters
                  elevation={0}
                  sx={{
                    "&:before": { display: "none" },
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      minHeight: 52,
                      "&.Mui-expanded": { minHeight: 52 },
                      "& .MuiAccordionSummary-content": {
                        my: 0,
                        alignItems: "center",
                      },
                    }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 600, flex: 1 }}>
                      {category}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ mr: 1 }}
                    >
                      {templates.length}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <List disablePadding>
                      {templates.map((template, index) => (
                        <ListItem key={`${category}-${index}`} disablePadding>
                          <ListItemButton
                            selected={selectedTemplate?.name === template.name && selectedTemplate?.body === template.body}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTemplate(template);
                            }}
                            sx={{
                              py: 1.5,
                              pl: 3,
                              pr: 2,
                              "&.Mui-selected": {
                                backgroundColor: "action.selected",
                              },
                            }}
                          >
                            <ListItemText
                              primary={template.name}
                              primaryTypographyProps={{
                                variant: "body1",
                                sx: {
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: 1.4,
                                },
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>

          {/* Right panel - Template Preview */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              p: 2,
            }}
          >
            {selectedTemplate ? (
              <>
                <Typography variant="h6" gutterBottom>
                  {selectedTemplate.name}
                </Typography>

                {selectedTemplate.subject && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      mb: 2,
                      backgroundColor: "action.hover",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      gutterBottom
                    >
                      SUBJECT LINE
                    </Typography>
                    <Typography variant="body1">
                      {selectedTemplate.subject}
                    </Typography>
                  </Paper>
                )}

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    flex: 1,
                    overflow: "auto",
                    backgroundColor: "action.hover",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    gutterBottom
                  >
                    EMAIL BODY
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "pre-wrap",
                      fontFamily: "monospace",
                      fontSize: "13px",
                    }}
                  >
                    {formatBody(selectedTemplate.body)}
                  </Typography>
                </Paper>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Variables like {"{{firstName}}"}, {"{{companyName}}"} will be
                    automatically replaced with member data. Placeholders in [brackets]
                    should be customized for your use case.
                  </Typography>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                }}
              >
                <Typography>Select a template to preview</Typography>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUseTemplate}
          disabled={!selectedTemplate}
        >
          Use Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTemplatesDialog;
export type { EmailTemplate } from "./emailTemplates";
